import { Children, type ReactNode } from "react";
import { AlertTriangle, Camera, Check, Clock, Flame, LoaderCircle, Plus, RotateCw, Trash2 } from "lucide-react";
import type { BollettaFuori } from "@/api/preventivo";
import { ELIMINA, GAS_FUORI, INVIO_NON_RIUSCITO, IN_INVIO, RIPROVA, fotoPagine, nomeEliminaBolletta, raggiuntoMassimo, rifareAiuta, testoNonLetta } from "./scritte";

const ICONA = "w-5 h-5 shrink-0";

const icone = {
  inLettura: <LoaderCircle className={`${ICONA} text-brand animate-spin motion-reduce:animate-none`} aria-hidden="true" />,
  inCoda: <Clock className={`${ICONA} text-muted-foreground`} aria-hidden="true" />,
  letta: <Check className={`${ICONA} text-brand`} aria-hidden="true" />,
  nonLetta: <AlertTriangle className={`${ICONA} text-amber-700`} aria-hidden="true" />,
  gas: <Flame className={`${ICONA} text-muted-foreground`} aria-hidden="true" />,
};

export function Icona({ tipo }: { tipo: keyof typeof icone }) {
  return icone[tipo];
}

const PULSANTE =
  "inline-flex h-11 items-center gap-1.5 rounded-lg border border-border bg-card px-3 text-sm font-medium text-text cursor-pointer hover:bg-surface transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand";

export function PulsanteRifai({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className={PULSANTE}>
      <Camera size={16} aria-hidden="true" /> Rifai la foto
    </button>
  );
}

export function PulsanteRiprova({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className={PULSANTE}>
      <RotateCw size={16} aria-hidden="true" /> {RIPROVA}
    </button>
  );
}

/** `nome`: il nome per il lettore di schermo, quando nella pagina ce n'è più d'uno (una bolletta caricata). */
export function PulsanteElimina({ onClick, nome }: { onClick: () => void; nome?: string }) {
  return (
    <button type="button" onClick={onClick} aria-label={nome} className={`${PULSANTE} !text-red-700`}>
      <Trash2 size={16} aria-hidden="true" /> {ELIMINA}
    </button>
  );
}

/** Un guasto da dire vicino al comando che è fallito, con «Riprova» che ripete la stessa azione ed «Elimina» che
 *  lascia perdere (senza «Riprova» se il server ha rifiutato per sempre). */
export function AvvisoErrore({ testo, onRiprova, onElimina }: { testo: string; onRiprova?: () => void; onElimina?: () => void }) {
  return (
    <div role="alert" className="flex w-full flex-wrap items-center gap-2 text-sm font-medium text-red-700">
      <AlertTriangle className={ICONA} aria-hidden="true" />
      <span className="min-w-0 break-words">{testo}</span>
      {onRiprova && <PulsanteRiprova onClick={onRiprova} />}
      {onElimina && <PulsanteElimina onClick={onElimina} />}
    </div>
  );
}

export function PulsantePagina({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className={PULSANTE}>
      <Plus size={16} aria-hidden="true" /> Aggiungi pagina
    </button>
  );
}

/** Una riga dell'elenco «Bollette caricate»: icona, nome del file, stato in parole. */
export function Voce({ icona, nome, stato, live = false, children }: { icona: ReactNode; nome: string; stato: string; live?: boolean; children?: ReactNode }) {
  return (
    <li className="flex items-start gap-3 rounded-lg border border-border bg-card p-3">
      <span className="mt-0.5">{icona}</span>
      <div className="min-w-0 flex-1">
        <p className="text-[15px] font-medium text-text break-words">{nome}</p>
        {stato && <p className="text-sm text-muted-foreground" role={live ? "alert" : undefined}>{stato}</p>}
        {Children.toArray(children).length > 0 && <div className="mt-2 flex flex-wrap gap-2">{children}</div>}
      </div>
    </li>
  );
}

/** Una bolletta fotografata che aspetta di arrivare: «In invio»; se non parte, solo «Invio non riuscito» con
 *  «Riprova» ed «Elimina» (solo «Elimina» se il server ha rifiutato per sempre). */
export function VoceFoto({ voce, massimo, onRiprova, onElimina }: {
  voce: { id: string; pagine: number; fallito: boolean; rifiutato?: boolean; tetto?: boolean };
  /** Il massimo di bollette del preventivo: serve alla frase del tetto. */
  massimo?: number;
  onRiprova: () => void;
  onElimina: () => void;
}) {
  return (
    <Voce icona={icone.inCoda} nome={fotoPagine(voce.pagine)} stato={voce.fallito ? "" : IN_INVIO}>
      {voce.fallito && (
        <AvvisoErrore testo={voce.tetto && massimo ? raggiuntoMassimo(massimo) : INVIO_NON_RIUSCITO} onRiprova={voce.rifiutato ? undefined : onRiprova} onElimina={onElimina} />
      )}
    </Voce>
  );
}

/** Una bolletta fatta di foto (scattate col telefono, senza nome di file, o caricate): su un PDF il server rifiuta
 *  le pagine. */
function diFoto(b: BollettaFuori): boolean {
  return b.nome_file === null || /\.(jpe?g|png)$/i.test(b.nome_file);
}

interface Props {
  bollette: BollettaFuori[];
  onPagina: (id: string) => void;
  onRifai: (id: string) => void;
  /** «Elimina» su ogni voce (03/11): la pagina chiede conferma. */
  onElimina: (b: BollettaFuori) => void;
  /** Bollette con un comando fallito (pagina non inviata, eliminazione non riuscita): testo e «Riprova». */
  errori?: Record<string, { testo: string; onRiprova: () => void }>;
}

/** Le bollette che il server non ha ancora messo in un condominio, nello stato che dice lui. */
export function BolletteFuori({ bollette, onPagina, onRifai, onElimina, errori = {} }: Props) {
  return (
    <>
      {bollette.map((b) => {
        const nome = b.nome_file ?? fotoPagine(b.pagine);
        // «Aggiungi pagina» solo per una pagina dimenticata: su una foto non letta, dove c'è «Rifai la foto». Mai mentre
        // si carica: la riga cambia stato e il pulsante si sposterebbe sotto il dito.
        const pagina = b.stato === "non_letta" && rifareAiuta(b.motivo) && diFoto(b)
          ? <PulsantePagina onClick={() => onPagina(b.id)} /> : null;
        const err = errori[b.id];
        const avviso = err ? <AvvisoErrore testo={err.testo} onRiprova={err.onRiprova} /> : null;
        // «Elimina» solo sulle voci ferme: una in lettura o in coda cambia posto da sola e il tasto passerebbe sotto il dito a
        // un'altra bolletta (la conferma non dice quale); letta, ha il suo «Elimina» nella tabella o qui
        const elimina = <PulsanteElimina onClick={() => onElimina(b)} nome={nomeEliminaBolletta(null)} />;
        switch (b.stato) {
          case "in_lettura":
            return <Voce key={b.id} icona={icone.inLettura} nome={nome} stato="In lettura">{avviso}</Voce>;
          case "in_coda":
            return <Voce key={b.id} icona={icone.inCoda} nome={nome} stato="In coda">{avviso}</Voce>;
          case "gas":
            return <Voce key={b.id} icona={icone.gas} nome={nome} stato={GAS_FUORI}>{elimina}</Voce>;
          case "non_letta":
            return (
              <Voce key={b.id} icona={icone.nonLetta} nome={nome} stato={testoNonLetta(b.motivo)}>
                {rifareAiuta(b.motivo) && <PulsanteRifai onClick={() => onRifai(b.id)} />}
                {pagina}
                {elimina}
                {avviso}
              </Voce>
            );
        }
      })}
    </>
  );
}
